/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import { HelpOutline } from "@mui/icons-material";
import { Box, CardContent, IconButton, Tooltip } from "@mui/material";
import axios from "axios";
import expressConfig from "src/config/express.json";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import { useMutation } from "@apollo/client";
import ADD_PAGE from "src/graphql/mutation/settings/addPage";
import GET_PAGES from "src/graphql/query/settings/getPages";
import { useTour } from "src/context/global/TourContext";
import { useSnackbar } from "notistack";
import UPDATE_USER_GUIDE from "src/graphql/mutation/user_guide/updateUserGuide";
import GET_USER_GUIDE from "src/graphql/query/user_guide/getUserGuide";
import EXCHANGE_FACEBOOK_TOKEN from "src/graphql/mutation/facebook/exchangeFacebookToken";
import SUBSCRIBE_FACEBOOK_PAGES from "src/graphql/mutation/facebook/subscribeFacebookPages";
import { useSelector } from "react-redux";
import FacebookInstructionsModal from "./facebookInstructionsModal";
import NotConnectedBox from "./notConnectedBox";
import ConnectedBox from "./connectedBox";
import ErrorBox from "./errorBox";
import useStyle from "../Styles";

declare global {
  interface Window {
    FB: any;
    fbq: any;
  }
}

interface IPage {
  name: string;
  pageId: string;
  accesstoken: string;
  picture: string;
  mainSuperAdminId: string;
  source: string;
  email: string;
}

interface Props {
  setPagesData: (value: any) => void;
  loggedInUserDetail: any;
  setloggedInUserDetail: (value: any) => void;
  setErrorBoxHeight: any;
  isCollapsed?: boolean;
}
const ExternalPage: React.FC<Props> = ({
  setPagesData,
  loggedInUserDetail,
  setloggedInUserDetail,
  setErrorBoxHeight,
  isCollapsed = false,
}) => {
  const { classes } = useStyle();

  const { enqueueSnackbar } = useSnackbar();
  const { mainSuperAdminId } = useActiveUserDetail();

  const [pageExistingError, setPageExistingError] = useState<string | null>(
    null,
  );

  const handleExistingPagesError = (error: string) => {
    setPageExistingError(error);
  };

  const [
    addPages,
    { data: addPagesMutationResult, loading: addPagesMutationLoading },
  ] = useMutation(ADD_PAGE);

  const [exchangeFacebookToken] = useMutation(EXCHANGE_FACEBOOK_TOKEN);
  const [subscribeFacebookPages] = useMutation(SUBSCRIBE_FACEBOOK_PAGES);

  const env = process.env.NODE_ENV || "development";
  const config = expressConfig[env];
  const SOURCE = "facebook";
  const RESPONSE_TYPE = "token";

  const [modifiedAddedPages, setModifiedAddedPages] = useState<IPage[]>([]);
  const [savedMetaId, setSavedMetaId] = useState("");
  const { me }: any = useSelector((state: any) => state.me);

  const [updateUserGuide] = useMutation(UPDATE_USER_GUIDE);
  const modalRef = React.useRef<any>(null);
  const hasAutoOpened = React.useRef(false);

  const updatePagesFunc = useCallback(
    (pagesAray: IPage[]) => {
      setPagesData((prev: any[]) => {
        // Filter new page IDs that are not in the previous state
        const newPages = pagesAray.filter(
          (item: { pageId: any }) =>
            !prev.some((i: any) => i.pageId === item.pageId),
        );

        // Get removed page IDs for the logged-in user against their metaId
        const removedPageIds = prev
          .filter(
            (item: { metaId: string; pageId: string }) =>
              item.metaId === savedMetaId,
          )
          .filter(
            (item: { pageId: string }) =>
              !pagesAray.some(
                (j: { pageId: string }) => j.pageId === item.pageId,
              ),
          )
          .map((item: { pageId: string }) => item.pageId);

        // Update the existing pages with the new `isSelected` status
        const updatedPages = prev.map((j: { pageId: any }) => {
          if (removedPageIds.includes(j.pageId)) {
            return {
              ...j,
              isSelected: false,
            };
          }
          return j;
        });

        return [...newPages, ...updatedPages];
      });
    },
    [savedMetaId, setPagesData],
  );

  const { startPostFbConnectionTour, guideData, guideLoading } = useTour();

  useEffect(() => {
    if (!addPagesMutationResult?.addpages) return;

    const { success, error, result } = addPagesMutationResult.addpages;
    let successfullyAddedCount = 0;

    if (success === 0 && error) {
      handleExistingPagesError(error);

      let filteredPages = modifiedAddedPages;
      if (result) {
        try {
          const erroredIds = JSON.parse(result);
          if (Array.isArray(erroredIds)) {
            filteredPages = modifiedAddedPages.filter(
              (pageObj) => !erroredIds.includes(pageObj.pageId),
            );
          }
        } catch (e) {
          // Fallback to name-based filtering if result is not valid JSON
          const erroredPages = error
            .split("has")[0]
            .split(",")
            .map((page: string) => page.trim());

          filteredPages = modifiedAddedPages.filter(
            (pageObj) => !erroredPages.includes(pageObj.name),
          );
        }
      }

      updatePagesFunc(filteredPages);
      successfullyAddedCount = filteredPages.length;
    } else {
      updatePagesFunc(modifiedAddedPages);
      successfullyAddedCount = modifiedAddedPages.length;
    }

    // Start tour if we actually connected at least one page
    if (
      successfullyAddedCount > 0 &&
      !guideData?.getUserGuide?.postFbConnectionCompleted
    ) {
      setTimeout(() => {
        startPostFbConnectionTour();
      }, 1000);
    }
  }, [addPagesMutationResult, guideData]);

  // Auto-open instructions modal on first visit
  useEffect(() => {
    if (
      !guideLoading &&
      guideData?.getUserGuide?.facebookGuideCompleted === false &&
      !hasAutoOpened.current &&
      modalRef.current
    ) {
      hasAutoOpened.current = true;
      modalRef.current.handleOpen();
    }
  }, [guideLoading, guideData]);

  const getAccessTokenByCodeForFacebook = async (
    id: string,
    accessToken: string,
    email: string,
  ) => {
    try {
      // Recursive helper to paginate through ALL Facebook pages.
      // Avoids `no-await-in-loop` by using recursion instead of a while loop.
      type FbAccountsResponse = { data: any[]; paging?: { next?: string } };
      const fetchAllFbPages = async (
        url: string,
        accumulated: any[] = [],
      ): Promise<any[]> => {
        const { data: fbResponse }: { data: FbAccountsResponse } =
          await axios.get(url);
        const all = [...accumulated, ...(fbResponse.data || [])];
        return fbResponse.paging?.next
          ? fetchAllFbPages(fbResponse.paging.next, all)
          : all;
      };

      const pagesData = await fetchAllFbPages(
        `https://graph.facebook.com/v21.0/${id}/accounts?fields=name,picture,access_token&access_token=${accessToken}`,
      );

      if (pagesData && pagesData.length) {
        // Subscribe pages to webhooks via backend
        const { data: subscriptionResult } = await subscribeFacebookPages({
          variables: {
            pageIds: pagesData.map((item: any) => item.id),
            accessTokens: pagesData.map((item: any) => item.access_token),
          },
        });

        if (subscriptionResult?.subscribeFacebookPages?.success === 0) {
          throw new Error(
            subscriptionResult.subscribeFacebookPages.error ||
              "Failed to subscribe pages",
          );
        }

        // ── Handle Partial Success ──────────────────────────────────────────
        // Some pages might have failed due to 2FA or permissions.
        // We filter the list to only add successfully subscribed pages to the CRM.
        let successfulPages = pagesData;
        const resultRaw = subscriptionResult?.subscribeFacebookPages?.result;

        if (resultRaw) {
          try {
            const { succeeded, failed } = JSON.parse(resultRaw);

            // Filter successfully subscribed pages
            successfulPages = pagesData.filter((p: any) =>
              succeeded.includes(p.id),
            );

            // Show detailed warnings for each failed page
            if (failed && failed.length > 0) {
              failed.forEach((failObj: { pageId: string; error: string }) => {
                const pageName =
                  pagesData.find((p: any) => p.id === failObj.pageId)?.name ||
                  failObj.pageId;

                enqueueSnackbar(
                  `Failed to connect "${pageName}": ${failObj.error}`,
                  {
                    variant: "warning",
                    autoHideDuration: 15000,
                  },
                );
              });
            }
          } catch (e) {
            console.error("Failed to parse subscription result", e);
          }
        }

        const createPageObjectValue = successfulPages.map((item: any) => ({
          name: item.name,
          pageId: item.id,
          accesstoken: item.access_token,
          picture: item.picture.data.url,
          mainSuperAdminId: String(mainSuperAdminId),
          source: SOURCE,
          email,
          metaId: id,
        }));

        addPages({
          variables: {
            input: createPageObjectValue,
          },
          refetchQueries: [
            {
              query: GET_PAGES,
              variables: {
                mainSuperAdminId,
                source: "facebook",
                onlySelected: false,
              },
            },
          ],
        });

        setModifiedAddedPages([...createPageObjectValue]);
        setSavedMetaId(id);
      }
    } catch (error: any) {
      enqueueSnackbar(
        error?.message || "Failed to connect Facebook page. Please try again.",
        {
          variant: "error",
        },
      );
    }
  };

  const openFacebookBuisnessLogin = async () => {
    try {
      const handleFbResponse = async (response: any) => {
        if (response.authResponse) {
          const { accessToken } = response.authResponse;
          try {
            const user = await axios.get(
              `https://graph.facebook.com/v21.0/me?access_token=${accessToken}&fields=id,name,email,picture.width(640).height(640)`,
            );
            setloggedInUserDetail(user.data);
            const { id, email } = user.data;

            // Exchange token via secure backend mutation
            const { data: tokenData } = await exchangeFacebookToken({
              variables: {
                shortLivedToken: accessToken,
                userId: id,
              },
            });

            if (!tokenData?.exchangeFacebookToken?.success) {
              throw new Error(
                tokenData?.exchangeFacebookToken?.error ||
                  "Token exchange failed",
              );
            }

            const newtoken = tokenData.exchangeFacebookToken.longLivedToken;
            await getAccessTokenByCodeForFacebook(id, newtoken, email);
          } catch (e: any) {
            console.log(e, "error for access token");
            enqueueSnackbar(
              e?.message || "Failed to connect Facebook. Please try again.",
              {
                variant: "error",
              },
            );
          }
        } else {
          enqueueSnackbar("Facebook login was cancelled or failed.", {
            variant: "error",
          });
        }
      };

      await new Promise((resolve, reject) => {
        window.FB.login(
          (response: any) => {
            handleFbResponse(response).then(resolve).catch(reject);
          },
          {
            config_id: config.FACEBOOK_CONFIG_ID,
            response_type: RESPONSE_TYPE,
            override_default_response_type: true,
            auth_type: "reauthenticate",
            scope: "email,public_profile",
          },
        );
      });
    } catch (e: any) {
      console.log("error in facebook login", e);
      enqueueSnackbar(
        e?.message || "Failed to initiate Facebook login. Please try again.",
        {
          variant: "error",
        },
      );
    }
  };

  const handleLogoutFacebook = () => {
    setloggedInUserDetail(null);
    try {
      if (window.FB) {
        window.FB.logout();
      }
    } catch (err) {
      console.warn("FB logout error", err);
    }
  };

  return (
    <CardContent className={classes.cardContent} id="tour-channel-section">
      <Box className={classes.upperCompStyle}>
        {!loggedInUserDetail ? (
          <Box sx={{ position: "relative", width: "100%" }}>
            <NotConnectedBox
              openFacebookBuisnessLogin={openFacebookBuisnessLogin}
              handleOpenInstructions={() => modalRef.current?.handleOpen()}
              isCollapsed={isCollapsed}
            />
          </Box>
        ) : (
          <Box sx={{ position: "relative", width: "100%" }}>
            <Box
              sx={{
                position: "absolute",
                right: 0,
                top: 0,
                zIndex: 5,
                display: isCollapsed ? "none" : "block",
              }}
            >
              <Tooltip title="Connection Instructions">
                <IconButton
                  size="small"
                  onClick={() => modalRef.current?.handleOpen()}
                  sx={{
                    color: "#4D1277",
                    opacity: 0.7,
                    "&:hover": { opacity: 1 },
                  }}
                >
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <ConnectedBox
              user={loggedInUserDetail}
              logout={handleLogoutFacebook}
              handleAddMorePages={openFacebookBuisnessLogin}
              loading={addPagesMutationLoading}
              isCollapsed={isCollapsed}
            />
          </Box>
        )}
        <FacebookInstructionsModal
          ref={modalRef}
          setModalState={(open: boolean) => {
            (async () => {
              if (
                !open &&
                me?.id &&
                guideData?.getUserGuide?.facebookGuideCompleted === false
              ) {
                try {
                  await updateUserGuide({
                    variables: {
                      agentId: me.id,
                      facebookGuideCompleted: true,
                    },
                    refetchQueries: [
                      { query: GET_USER_GUIDE, variables: { agentId: me.id } },
                    ],
                  });
                } catch (error) {
                  console.error(
                    "Failed to update facebook guide status",
                    error,
                  );
                }
              }
            })();
          }}
        />
      </Box>
      <ErrorBox
        error={pageExistingError || ""}
        handleClick={() => handleExistingPagesError("")}
        setErrorHeight={setErrorBoxHeight}
      />
    </CardContent>
  );
};

export default ExternalPage;
